// Cockpit home page. A greeting, then the sales dashboard for users who hold
// OMNI Performance. The per-tool status cards that used to sit below it were
// removed (Steven, 2026-10-30); each tool shows its own status on its page.

import { Navigate } from 'react-router-dom';
import { FilterLabel } from '@/components/FilterLabel';
import { useSession } from '@/lib/useSession';
import { isStoreOnlyUser } from '@/lib/useStores';
import { findTool } from '@/data/tools.config';
import { isToolPermitted } from '@/lib/toolVisibility';
import { firstNameOf } from '@/lib/nameUtils';
import { SalesDashboard } from '@/pages/home/SalesDashboard';

function greetingFor(date: Date): string {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function todayLabel(): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date());
}

export function Home() {
  const { user } = useSession();
  const firstName = firstNameOf(user);
  const greeting = greetingFor(new Date());

  const perms = user?.permissions ?? [];
  const superAdmin = !!user?.superAdmin;
  // Gate the sales dashboard off the SAME catalog + accessSlug model the sidebar
  // uses (toolVisibility.ts), rather than a bespoke capability string, so this
  // page cannot drift from what the user can actually reach (Steven,
  // 2026-08-09 binary access-grant migration: the old raw capability strings
  // this checked, e.g. 'commerce:read', are never granted anymore).
  const canTool = (deptId: string, toolId: string) => {
    const found = findTool(deptId, toolId);
    return !!found && isToolPermitted(found.tool, perms, superAdmin);
  };

  // Store-only accounts land on the store home: the home page is
  // built for HQ tools they cannot see.
  if (isStoreOnlyUser(user?.permissions)) return <Navigate replace to="/stores" />;

  return (
    <div className="pb-20">
      <header
        className="border-b border-amp-line dark:border-amp-dark-line"
        style={{ paddingBottom: 28, marginBottom: 28 }}
      >
        <FilterLabel as="div" className="!mb-2.5">
          Cockpit &middot; {todayLabel()}
        </FilterLabel>
        <h1
          className="text-amp-ink dark:text-amp-dark-ink"
          style={{
            fontWeight: 600,
            fontSize: 42,
            lineHeight: 1.05,
            letterSpacing: '-0.02em',
            margin: 0,
          }}
        >
          {greeting}, {firstName}.
        </h1>
      </header>

      {/* Sales dashboard: same access as OMNI Performance (stores.performance). */}
      {canTool('reports', 'performance') && <SalesDashboard />}
    </div>
  );
}
